/**
 * Advanced debugging utilities for PODx
 * Provides comprehensive debugging, performance monitoring, and development tools
 */

import { logger } from '../logger/index.js';
import type { LogContext } from '../logger/index.js';
import type {
  DebugSession,
  DebugOperation,
  DebugError,
  DebugWarning,
  PerformanceMetric,
  DebugSessionId,
  PerformanceMetricId
} from '../types/common.js';
import {
  generateDebugSessionId,
  generatePerformanceMetricId,
  generateId
} from '../types/branded.js';

// Debug configuration
export interface DebugConfig {
  readonly enabled: boolean;
  readonly logLevel: 'trace' | 'debug' | 'info' | 'warn' | 'error';
  readonly enablePerformanceMonitoring: boolean;
  readonly enableMemoryTracking: boolean;
  readonly enableStackTraceCapture: boolean;
  readonly maxSessionDuration: number; // milliseconds
  readonly maxOperationsPerSession: number;
  readonly enableRemoteDebugging: boolean;
  readonly remoteDebugPort?: number;
}

// Performance monitoring utilities
export class PerformanceMonitor {
  private static readonly activeMetrics = new Map<PerformanceMetricId, PerformanceMetric>();
  private static readonly completedMetrics: PerformanceMetric[] = [];

  /**
   * Start performance monitoring for an operation
   */
  static startMetric(name: string, context?: LogContext): PerformanceMetricId {
    const id = generatePerformanceMetricId();
    const metric: PerformanceMetric = {
      id,
      name,
      startTime: performance.now(),
      context
    };

    this.activeMetrics.set(id, metric);
    logger.debug(`Performance metric started: ${name}`, { metricId: id, ...context });
    
    return id;
  }

  /**
   * End performance monitoring and return the metric
   */
  static endMetric(id: PerformanceMetricId): PerformanceMetric | null {
    const metric = this.activeMetrics.get(id);
    if (!metric) {
      logger.warn(`Performance metric not found: ${id}`);
      return null;
    }

    const endTime = performance.now();
    const completedMetric: PerformanceMetric = {
      ...metric,
      endTime,
      duration: endTime - metric.startTime,
      memoryUsage: process.memoryUsage()
    };

    this.activeMetrics.delete(id);
    this.completedMetrics.push(completedMetric);

    logger.debug(`Performance metric completed: ${metric.name}`, {
      metricId: id,
      duration: completedMetric.duration,
      memoryUsage: completedMetric.memoryUsage,
      ...metric.context
    });

    return completedMetric;
  }

  /**
   * Get performance summary for a session
   */
  static getPerformanceSummary(): {
    totalMetrics: number;
    averageDuration: number;
    slowestOperations: PerformanceMetric[];
    memoryPeak: NodeJS.MemoryUsage | null;
  } {
    const totalMetrics = this.completedMetrics.length;
    const totalDuration = this.completedMetrics.reduce((sum, metric) => sum + (metric.duration || 0), 0);
    const averageDuration = totalMetrics > 0 ? totalDuration / totalMetrics : 0;

    const slowestOperations = [...this.completedMetrics]
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))
      .slice(0, 10);

    const memoryPeak = this.completedMetrics.reduce<NodeJS.MemoryUsage | null>((peak, metric) => {
      if (!metric.memoryUsage) return peak;
      if (!peak) return metric.memoryUsage;
      
      return {
        rss: Math.max(peak.rss, metric.memoryUsage.rss),
        heapTotal: Math.max(peak.heapTotal, metric.memoryUsage.heapTotal),
        heapUsed: Math.max(peak.heapUsed, metric.memoryUsage.heapUsed),
        external: Math.max(peak.external, metric.memoryUsage.external),
        arrayBuffers: Math.max(peak.arrayBuffers, metric.memoryUsage.arrayBuffers)
      };
    }, null);

    return {
      totalMetrics,
      averageDuration,
      slowestOperations,
      memoryPeak
    };
  }

  /**
   * Clear all metrics (useful for testing or session cleanup)
   */
  static clearMetrics(): void {
    this.activeMetrics.clear();
    this.completedMetrics.length = 0;
  }
}

// Debug session manager
export class DebugSessionManager {
  private static readonly activeSessions = new Map<DebugSessionId, DebugSession>();
  private static readonly config: DebugConfig = {
    enabled: process.env.NODE_ENV === 'development' || process.env.DEBUG === 'true',
    logLevel: (process.env.DEBUG_LEVEL as any) || 'debug',
    enablePerformanceMonitoring: true,
    enableMemoryTracking: true,
    enableStackTraceCapture: true,
    maxSessionDuration: 30 * 60 * 1000, // 30 minutes
    maxOperationsPerSession: 1000,
    enableRemoteDebugging: process.env.REMOTE_DEBUG === 'true',
    remoteDebugPort: process.env.REMOTE_DEBUG_PORT ? parseInt(process.env.REMOTE_DEBUG_PORT) : undefined
  };

  /**
   * Create a new debug session
   */
  static createSession(context: LogContext): DebugSessionId {
    if (!this.config.enabled) {
      return 'disabled' as DebugSessionId;
    }

    const id = generateDebugSessionId();
    const session: DebugSession = {
      id,
      startTime: Date.now(),
      context,
      metrics: new Map(),
      operations: [],
      errors: [],
      warnings: []
    };

    this.activeSessions.set(id, session);
    logger.debug('Debug session created', { sessionId: id, ...context });

    // Set up session cleanup
    setTimeout(() => {
      this.cleanupSession(id);
    }, this.config.maxSessionDuration);

    return id;
  }

  /**
   * Add an operation to a debug session
   */
  static addOperation(
    sessionId: DebugSessionId,
    operation: Omit<DebugOperation, 'id' | 'startTime' | 'status'>
  ): string {
    if (!this.config.enabled || sessionId === 'disabled') return 'disabled';

    const session = this.activeSessions.get(sessionId);
    if (!session) {
      logger.warn('Debug session not found', { sessionId });
      return 'not-found';
    }

    if (session.operations.length >= this.config.maxOperationsPerSession) {
      logger.warn('Maximum operations per session reached', { sessionId });
      return 'limit-exceeded';
    }

    const operationId = generateId('op');
    const fullOperation: DebugOperation = {
      id: operationId,
      startTime: Date.now(),
      status: 'running',
      ...operation
    };

    session.operations.push(fullOperation);
    logger.debug('Debug operation added', { sessionId, operationId, ...operation.context });

    return operationId;
  }

  /**
   * Update operation status
   */
  static updateOperation(
    sessionId: DebugSessionId,
    operationId: string,
    updates: Partial<Pick<DebugOperation, 'status' | 'endTime' | 'duration' | 'error'>>
  ): void {
    if (!this.config.enabled || sessionId === 'disabled') return;

    const session = this.activeSessions.get(sessionId);
    if (!session) return;

    const operation = session.operations.find(op => op.id === operationId);
    if (!operation) return;

    Object.assign(operation, updates);
    
    if (updates.endTime && operation.startTime) {
      operation.duration = updates.endTime - operation.startTime;
    }

    logger.debug('Debug operation updated', { 
      sessionId, 
      operationId, 
      status: operation.status,
      duration: operation.duration 
    });
  }

  /**
   * Add error to debug session
   */
  static addError(
    sessionId: DebugSessionId,
    error: Error,
    context?: LogContext,
    operation?: string
  ): void {
    if (!this.config.enabled || sessionId === 'disabled') return;

    const session = this.activeSessions.get(sessionId);
    if (!session) return;

    const debugError: DebugError = {
      id: generateId('error'),
      timestamp: Date.now(),
      error,
      context,
      stack: this.config.enableStackTraceCapture ? error.stack : undefined,
      operation
    };

    session.errors.push(debugError);
    logger.error('Debug error captured', { 
      sessionId, 
      errorId: debugError.id, 
      error: error.message,
      operation,
      ...context 
    });
  }

  /**
   * Add warning to debug session
   */
  static addWarning(
    sessionId: DebugSessionId,
    message: string,
    context?: LogContext,
    operation?: string
  ): void {
    if (!this.config.enabled || sessionId === 'disabled') return;

    const session = this.activeSessions.get(sessionId);
    if (!session) return;

    const debugWarning: DebugWarning = {
      id: generateId('warning'),
      timestamp: Date.now(),
      message,
      context,
      operation
    };

    session.warnings.push(debugWarning);
    logger.warn('Debug warning captured', { 
      sessionId, 
      warningId: debugWarning.id, 
      message,
      operation,
      ...context 
    });
  }

  /**
   * Get session summary
   */
  static getSessionSummary(sessionId: DebugSessionId): {
    session: DebugSession | null;
    performance: ReturnType<typeof PerformanceMonitor.getPerformanceSummary>;
    health: {
      errorRate: number;
      warningCount: number;
      averageOperationDuration: number;
      memoryUsage: NodeJS.MemoryUsage;
    };
  } | null {
    if (!this.config.enabled || sessionId === 'disabled') return null;

    const session = this.activeSessions.get(sessionId);
    if (!session) return null;

    const performance = PerformanceMonitor.getPerformanceSummary();
    
    const totalOperations = session.operations.length;
    const failedOperations = session.operations.filter(op => op.status === 'failed').length;
    const errorRate = totalOperations > 0 ? failedOperations / totalOperations : 0;
    
    const completedOperations = session.operations.filter(op => op.duration !== undefined);
    const averageOperationDuration = completedOperations.length > 0 
      ? completedOperations.reduce((sum, op) => sum + (op.duration || 0), 0) / completedOperations.length
      : 0;

    return {
      session,
      performance,
      health: {
        errorRate,
        warningCount: session.warnings.length,
        averageOperationDuration,
        memoryUsage: process.memoryUsage()
      }
    };
  }

  /**
   * Cleanup expired sessions
   */
  private static cleanupSession(sessionId: DebugSessionId): void {
    const session = this.activeSessions.get(sessionId);
    if (!session) return;

    const duration = Date.now() - session.startTime;
    logger.debug('Debug session cleanup', { 
      sessionId, 
      duration,
      operationsCount: session.operations.length,
      errorsCount: session.errors.length,
      warningsCount: session.warnings.length
    });

    this.activeSessions.delete(sessionId);
  }

  /**
   * Get all active sessions
   */
  static getActiveSessions(): DebugSessionId[] {
    return Array.from(this.activeSessions.keys());
  }

  /**
   * Clear all sessions (useful for testing)
   */
  static clearAllSessions(): void {
    this.activeSessions.clear();
    PerformanceMonitor.clearMetrics();
  }
}

// Debug decorators and utilities
export function debugOperation(operationName: string, context?: LogContext) {
  return function (target: any, propertyName: string, descriptor: PropertyDescriptor) {
    const method = descriptor.value;

    descriptor.value = async function (...args: any[]) {
      const sessionId = DebugSessionManager.createSession({
        operation: operationName,
        method: propertyName,
        ...context
      });

      const operationId = DebugSessionManager.addOperation(sessionId, {
        name: operationName,
        context: { method: propertyName, args: args.length }
      });

      const metricId = PerformanceMonitor.startMetric(operationName, {
        method: propertyName,
        sessionId
      });

      try {
        const result = await method.apply(this, args);
        
        DebugSessionManager.updateOperation(sessionId, operationId, {
          status: 'completed',
          endTime: Date.now()
        });

        PerformanceMonitor.endMetric(metricId);
        return result;
      } catch (error) {
        DebugSessionManager.addError(sessionId, error as Error, {
          method: propertyName,
          args: args.length
        }, operationName);

        DebugSessionManager.updateOperation(sessionId, operationId, {
          status: 'failed',
          endTime: Date.now(),
          error: error as Error
        });

        PerformanceMonitor.endMetric(metricId);
        throw error;
      }
    };

    return descriptor;
  };
}

// Development debugging utilities
export class DevDebugger {
  /**
   * Interactive debugging helper
   */
  static inspect(value: any, label?: string): any {
    if (process.env.NODE_ENV === 'development') {
      console.group(label || 'Debug Inspection');
      console.log('Type:', typeof value);
      console.log('Value:', value);
      if (typeof value === 'object' && value !== null) {
        console.log('Keys:', Object.keys(value));
        console.log('Prototype:', Object.getPrototypeOf(value).constructor.name);
      }
      console.groupEnd();
    }
    return value;
  }

  /**
   * Memory usage snapshot
   */
  static memorySnapshot(label?: string): NodeJS.MemoryUsage {
    const usage = process.memoryUsage();
    if (process.env.NODE_ENV === 'development') {
      console.log(`Memory Snapshot${label ? ` - ${label}` : ''}:`, {
        rss: `${Math.round(usage.rss / 1024 / 1024)} MB`,
        heapTotal: `${Math.round(usage.heapTotal / 1024 / 1024)} MB`,
        heapUsed: `${Math.round(usage.heapUsed / 1024 / 1024)} MB`,
        external: `${Math.round(usage.external / 1024 / 1024)} MB`,
        arrayBuffers: `${Math.round(usage.arrayBuffers / 1024 / 1024)} MB`
      });
    }
    return usage;
  }

  /**
   * Performance timing helper
   */
  static async time<T>(name: string, operation: () => Promise<T>): Promise<T> {
    const start = performance.now();
    try {
      const result = await operation();
      const duration = performance.now() - start;
      logger.debug(`Performance: ${name}`, { duration: `${duration.toFixed(2)}ms` });
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      logger.error(`Performance: ${name} failed`, { duration: `${duration.toFixed(2)}ms` }, error as Error);
      throw error;
    }
  }
}

// Export main debugging utilities
export const debug = {
  session: DebugSessionManager,
  performance: PerformanceMonitor,
  dev: DevDebugger,
  operation: debugOperation
};

// Initialize debugging if enabled
if (process.env.NODE_ENV === 'development' || process.env.DEBUG === 'true') {
  logger.info('Debug mode enabled', {
    performanceMonitoring: DebugSessionManager['config'].enablePerformanceMonitoring,
    memoryTracking: DebugSessionManager['config'].enableMemoryTracking,
    remoteDebugging: DebugSessionManager['config'].enableRemoteDebugging
  });
}
